/* eslint-disable react-refresh/only-export-components -- exports the pure buildRestoreAddCreatives helper alongside the modal for direct unit testing */
// CTV-661 (ADR-0012 revival): "Restore previous commercial" confirmation.
//
// Brings back exactly the delivering set that existed immediately BEFORE the
// currently-open swap, one click, one confirmation. Lives at the bottom of
// the Edit Commercial tab and only for the two "gated approved" states:
//   - state 2 (under_review): the pending swap is removed in the same save so
//     its 100% shares don't collide with the restored set's 100%
//     (assertSegmentSharesValid's 200%-share guard). `removeCreativeIds` =
//     pending swap ids.
//   - state 4 (needs_fix): the voided swap is already out of the Live Set;
//     re-adding the prior set auto-stamps its `resolved_at` and clears "Not
//     Approved." `removeCreativeIds` = [] (retiring it would launder the
//     rejection into a prior-approval via hasPriorApproval).
// The state-aware `removeCreativeIds` is computed by the caller and passed in.
//
// The restored creatives fingerprint-match approved library rows, so
// update-creative-set inserts them `active` (no re-review) and the swap push
// fires, delivery resumes today. Reversible (the buyer can swap again), so
// there is no type-to-confirm gate.

import React from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import type {
  PublisherConfig,
  SelfServiceCreativeRow,
  SelfServiceSubmissionRow,
} from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import {
  seedSegmentSharesForNewCreative,
  singleSegmentShareForWindow,
  type CreativeWindow,
} from './shared/rotationSegments';
import { useCreativePreviewUrl } from './shared/useCreativePreviewUrl';

interface RestorePreviousModalProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  // The retired pre-swap creatives to bring back (already filtered to
  // submission.restorablePreSwapCreativeIds by the caller).
  creatives: SelfServiceCreativeRow[];
  // State-aware ids to remove in the SAME save. See the file header.
  removeCreativeIds: string[];
  onCancel: () => void;
  onRestored: () => void;
}

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function maxYmd(a: string, b: string): string {
  return a > b ? a : b;
}

// CTV-661: reconstruct valid segment_shares for the restored set over the
// common restore window [restoreStart, restoreEnd]. Every restored creative
// gets that full window (ADR-0027 restore step), so a single creative is 100%
// and multiple creatives split evenly, both valid by construction via the
// same rotation-segment seeding the wizard/edit panel use. Exact prior
// weighting isn't reconstructed because all rows are re-based to one window;
// the even split is the natural partition and always satisfies the server's
// assertSegmentSharesValid.
export function buildRestoreAddCreatives(
  creatives: SelfServiceCreativeRow[],
  restoreStart: string,
  restoreEnd: string,
): Array<{
  filename: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  clickThroughUrl?: string;
  segmentShares: Array<{ startDate: string; endDate: string; share: number }>;
  startDate: string;
  endDate: string;
}> {
  let windows: CreativeWindow[] = [];
  for (const c of creatives) {
    windows = seedSegmentSharesForNewCreative(windows, {
      id: c.id,
      startDate: restoreStart,
      endDate: restoreEnd,
    });
  }
  const sharesById = new Map(windows.map((w) => [w.id, w.segmentShares]));
  return creatives.map((c) => ({
    filename: c.filename,
    storagePath: c.storage_path,
    mimeType: c.mime_type ?? 'video/mp4',
    sizeBytes: c.size_bytes ?? 0,
    clickThroughUrl: c.click_through_url?.trim() || undefined,
    segmentShares:
      sharesById.get(c.id) ?? singleSegmentShareForWindow(restoreStart, restoreEnd),
    startDate: restoreStart,
    endDate: restoreEnd,
  }));
}

const RestoreCreativeRow: React.FC<{ slug: string; creative: SelfServiceCreativeRow }> = ({
  slug,
  creative,
}) => {
  const { url, posterUrl, loading } = useCreativePreviewUrl(slug, creative.storage_path);
  return (
    <li className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5">
      <div className="w-20 shrink-0">
        {loading || !url ? (
          <div className="aspect-video w-full rounded bg-slate-100 animate-pulse" />
        ) : (
          <video
            src={url}
            poster={posterUrl ?? undefined}
            muted
            playsInline
            preload="metadata"
            className="aspect-video w-full rounded bg-black object-cover"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-slate-900 truncate" title={creative.filename}>
          {creative.filename}
        </div>
        {creative.click_through_url ? (
          <div className="mt-0.5 text-[11px] text-slate-500 truncate" title={creative.click_through_url}>
            {creative.click_through_url}
          </div>
        ) : null}
      </div>
    </li>
  );
};

export function RestorePreviousModal({
  submission,
  cfg,
  creatives,
  removeCreativeIds,
  onCancel,
  onRestored,
}: RestorePreviousModalProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const campaignStart = submission.campaign?.startDate ?? '';
  const campaignEnd = submission.campaign?.endDate ?? '';
  const restoreStart = maxYmd(todayYmd(), campaignStart);
  const restoreEnd = campaignEnd;

  const multiple = creatives.length > 1;

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${submission.id}/update-creative-set`, {
        removeCreativeIds,
        addCreatives: buildRestoreAddCreatives(creatives, restoreStart, restoreEnd),
        updateExisting: [],
      });
      analytics.track(EVENTS.CREATIVE_SET_UPDATED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        added: creatives.length,
        updated: 0,
        removed: removeCreativeIds.length,
        restore: true,
      });
      onRestored();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore commercials');
      setBusy(false);
    }
  };

  // Portal to <body>: EditCampaignModal (this component's ancestor) centers its
  // dialog with a `-translate` transform AND is the scroll container, which
  // reparents `position: fixed` to that box, so an inline render lands at the
  // top of the scrolled panel instead of centered over the viewport. Portaling
  // escapes the transform (same fix EditCampaignModal itself uses). z-[60] sits
  // above the parent modal's z-50.
  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-[60]"
        onClick={(e) => {
          e.stopPropagation();
          if (!busy) onCancel();
        }}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[90%] max-w-[440px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
        style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="p-5">
          <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">
            {multiple ? 'Restore previous commercials?' : 'Restore previous commercial?'}
          </h3>
          <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
            {multiple ? 'These commercials' : 'This commercial'} will start running again for the rest of your
            campaign.
          </p>

          <ul className="mt-3.5 space-y-2 max-h-[240px] overflow-y-auto">
            {creatives.map((c) => (
              <RestoreCreativeRow key={c.id} slug={cfg.slug} creative={c} />
            ))}
          </ul>

          {error && (
            <p className="text-[12px] text-red-600 mt-3 leading-relaxed">{error}</p>
          )}

          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              Keep current
            </button>
            <button
              type="button"
              onClick={() => void onConfirm()}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px ${cfg.primaryColor}`,
              }}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {multiple ? 'Restore commercials' : 'Restore commercial'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default RestorePreviousModal;
